// BotControls.jsx (Updated for permission handling)
import React, { useState, useEffect } from "react";
import { Box, Button, ButtonGroup, Chip, Typography, Stack, LinearProgress } from "@mui/material";
import { PlayArrow, Stop, Refresh, Delete, Download } from "@mui/icons-material";
import { startBot, stopBot, restartBot, deleteBot, installPackages } from "../../services/botService";
import useAuth from "../../hooks/useAuth";

function BotControls({ bot, onAction, onDelete }) {
	const [loading, setLoading] = useState(false);
	const [permissions, setPermissions] = useState({
		canStart: false,
		canStop: false,
	});
	const { user } = useAuth();

	useEffect(() => {
		if (!bot || !user) return;

		// Check if user is bot owner or has permissions
		const isOwner = bot.user_id === user.id || (typeof bot.owner === "string" && user.username === bot.owner);

		// Expanded permission checking logic - look for permissions in multiple possible formats
		const hasStartPermission =
			bot.can_start === true || // Direct property
			(bot.permissions && bot.permissions.includes("start_bot")) || // Array format
			(user.permissions && user.permissions.includes("start_bot")) || // User permissions
			(Array.isArray(user.permissions) && user.permissions.some((p) => (p.bot_id === bot.id && p.can_start) || p === "start_bot")); // Object format

		const hasStopPermission =
			bot.can_stop === true || // Direct property
			(bot.permissions && bot.permissions.includes("stop_bot")) || // Array format
			(user.permissions && user.permissions.includes("stop_bot")) || // User permissions
			(Array.isArray(user.permissions) && user.permissions.some((p) => (p.bot_id === bot.id && p.can_stop) || p === "stop_bot")); // Object format

		setPermissions({
			canStart: isOwner || hasStartPermission,
			canStop: isOwner || hasStopPermission,
		});

		// Log for debugging
		console.log("Bot permissions check:", {
			isOwner,
			botUserId: bot.user_id,
			owner: bot.owner,
			userId: user.id,
			username: user.username,
			botCanStart: bot.can_start,
			botCanStop: bot.can_stop,
			userPermissions: user.permissions,
			botPermissions: bot.permissions,
			calculatedPermissions: {
				canStart: isOwner || hasStartPermission,
				canStop: isOwner || hasStopPermission,
			},
		});
	}, [bot, user]);

	const handleStart = async () => {
		try {
			setLoading(true);
			await startBot(bot.id);
			if (onAction) onAction("start");
		} catch (error) {
			console.error("Failed to start bot:", error);
			alert(`Failed to start bot: ${error.response?.data?.message || error.message}`);
		} finally {
			setLoading(false);
		}
	};

	const handleStop = async () => {
		try {
			setLoading(true);
			await stopBot(bot.id);
			if (onAction) onAction("stop");
		} catch (error) {
			console.error("Failed to stop bot:", error);
			alert(`Failed to stop bot: ${error.response?.data?.message || error.message}`);
		} finally {
			setLoading(false);
		}
	};

	const handleRestart = async () => {
		try {
			setLoading(true);
			await restartBot(bot.id);
			if (onAction) onAction("restart");
		} catch (error) {
			console.error("Failed to restart bot:", error);
			alert(`Failed to restart bot: ${error.response?.data?.message || error.message}`);
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async () => {
		if (window.confirm(`Are you sure you want to delete the bot "${bot.name}"? This cannot be undone.`)) {
			try {
				setLoading(true);
				await deleteBot(bot.id);
				if (onDelete) onDelete();
			} catch (error) {
				console.error("Failed to delete bot:", error);
				alert(`Failed to delete bot: ${error.response?.data?.message || error.message}`);
			} finally {
				setLoading(false);
			}
		}
	};

	const handleInstallPackages = async () => {
		try {
			setLoading(true);
			await installPackages(bot.id);
			if (onAction) onAction("install");
		} catch (error) {
			console.error("Failed to install packages:", error);
			alert(`Failed to install packages: ${error.response?.data?.message || error.message}`);
		} finally {
			setLoading(false);
		}
	};

	// If bot or user is not loaded yet, show loading state
	if (!bot || !user) {
		return <Box>Loading controls...</Box>;
	}

	// Debug info at render time
	console.log("Rendering controls with permissions:", permissions);

	// For testing - force enable buttons by setting permissions
	// const forceEnabled = true; // Uncomment for testing
	// const actualPermissions = forceEnabled ? { canStart: true, canStop: true } : permissions;

	return (
		<Box>
			{bot.status === "running" && (
				<Box sx={{ mb: 3 }}>
					<Typography variant="body2" color="text.secondary" gutterBottom>
						Resource Usage
					</Typography>

					<Box sx={{ mb: 2 }}>
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
							<Typography variant="body2">Memory</Typography>
							<Typography variant="body2" fontWeight="bold">
								{bot.memory || "N/A"}
							</Typography>
						</Box>
						<LinearProgress
							variant="determinate"
							value={parseInt(bot.memory || "0") / 10}
							sx={{
								height: 8,
								borderRadius: 4,
							}}
						/>
					</Box>

					<Box sx={{ mb: 2 }}>
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
							<Typography variant="body2">CPU</Typography>
							<Typography variant="body2" fontWeight="bold">
								{bot.cpu || "N/A"}
							</Typography>
						</Box>
						<LinearProgress
							variant="determinate"
							value={parseInt(bot.cpu || "0")}
							sx={{
								height: 8,
								borderRadius: 4,
							}}
						/>
					</Box>

					<Box>
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
							<Typography variant="body2">Uptime</Typography>
							<Typography variant="body2" fontWeight="bold">
								{bot.uptime || "N/A"}
							</Typography>
						</Box>
					</Box>
				</Box>
			)}

			<Stack spacing={2}>
				{/* Always render control buttons, but conditionally disable them */}
				<ButtonGroup variant="outlined" fullWidth>
					<Button
						startIcon={<PlayArrow />}
						color="success"
						disabled={!permissions.canStart || loading || bot.status === "running"}
						onClick={handleStart}
						sx={{ flexGrow: 1 }}
					>
						Start
					</Button>

					<Button
						startIcon={<Stop />}
						color="error"
						disabled={!permissions.canStop || loading || bot.status === "stopped"}
						onClick={handleStop}
						sx={{ flexGrow: 1 }}
					>
						Stop
					</Button>

					<Button
						startIcon={<Refresh />}
						color="primary"
						disabled={!permissions.canStart || !permissions.canStop || loading || bot.status === "stopped"}
						onClick={handleRestart}
						sx={{ flexGrow: 1 }}
					>
						Restart
					</Button>
				</ButtonGroup>

				{/* Only show for bot owners */}
				{(bot.user_id === user.id || (typeof bot.owner === "string" && user.username === bot.owner)) && (
					<>
						<Button variant="outlined" color="info" startIcon={<Download />} disabled={loading} onClick={handleInstallPackages} fullWidth>
							Install Packages
						</Button>

						<Button variant="outlined" color="error" startIcon={<Delete />} disabled={loading} onClick={handleDelete} fullWidth>
							Delete Bot
						</Button>
					</>
				)}
			</Stack>

			{/* Debug info - uncomment this to see permission details in UI
			<Box sx={{ mt: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
				<Typography variant="subtitle2">Permissions Debug Info:</Typography>
				<Typography variant="caption" component="pre" style={{ whiteSpace: "pre-wrap", overflow: "auto" }}>
					{JSON.stringify(
						{
							canStart: permissions.canStart,
							canStop: permissions.canStop,
							isOwner: bot.user_id === user.id || (typeof bot.owner === "string" && user.username === bot.owner),
							botUserId: bot.user_id,
							botOwner: bot.owner,
							userId: user.id,
							username: user.username,
						},
						null,
						2
					)}
				</Typography>
			</Box> */}
		</Box>
	);
}

export default BotControls;
